import { useState, useEffect } from 'react';
import { Row, Col, Alert, Button } from 'react-bootstrap';
import AppScriptAPI from '../services/appscriptAPI';
import { useUserPermissions } from '../contexts/UserPermissionsContext';
import { filterUsersByAdminScope } from '../utils/accessControl';
import DashStatCard from './admin/components/DashStatCard';
import UserBreakdownCard from './admin/components/UserBreakdownCard';
import ProvinceSubmissionsCard from './admin/components/ProvinceSubmissionsCard';
import RecentUsersCard from './admin/components/RecentUsersCard';
import QuickActionsCard from './admin/components/QuickActionsCard';

const SCOPE_LABELS = {
  province: 'Province-Level Overview',
  district: 'District-Level Overview',
  dsd:      'DS Division Overview',
};

const BREAKDOWN_TITLES = {
  Province:    'Submissions by Province',
  District:    'Submissions by District',
  'DS Division': 'Submissions by DS Division',
};

function Dashboard() {
  const { userPermissions } = useUserPermissions();
  const [users, setUsers] = useState([]);
  const [dash, setDash]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersResult, dashResult] = await Promise.allSettled([
        AppScriptAPI.getAllUsers(),
        AppScriptAPI.getDashboardData(),
      ]);

      if (usersResult.status === 'fulfilled' && usersResult.value.success) {
        setUsers(usersResult.value.users || []);
      } else {
        setError('Could not load user data.');
      }

      if (dashResult.status === 'fulfilled' && dashResult.value.success) {
        setDash(dashResult.value);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Derived values (scoped by admin permissions) ────────────────────────────
  const scopedActive    = filterUsersByAdminScope(users.filter(u => u.status === 'active'), userPermissions);
  const scopedSuspended = filterUsersByAdminScope(users.filter(u => u.status === 'suspended'), userPermissions);
  const allPending      = users.filter(u => u.status === 'pending');

  const totalUsers     = scopedActive.length + allPending.length + scopedSuspended.length;
  const activeUsers    = scopedActive.length;
  const pendingUsers   = allPending.length;
  const suspendedUsers = scopedSuspended.length;

  const scopedUsers = [...scopedActive, ...scopedSuspended, ...allPending];
  const adminCount  = scopedUsers.filter(u => u.access_type === 'admin').length;
  const editorCount = scopedUsers.filter(u => u.access_type === 'editor').length;
  const viewerCount = scopedUsers.filter(u => u.access_type === 'viewer').length;

  const recentUsers = [...scopedUsers]
    .filter(u => u.created_date)
    .sort((a, b) => new Date(b.created_date) - new Date(a.created_date))
    .slice(0, 6);

  const provinceData = dash?.provinceData
    ? [...dash.provinceData].sort((a, b) => b.count - a.count)
    : [];

  const scopeSubtitle = dash?.scope
    ? SCOPE_LABELS[dash.scope] || 'National CBR Program — System Overview'
    : 'National CBR Program — System Overview';

  const breakdownTitle = BREAKDOWN_TITLES[dash?.breakdownLabel] || 'Submissions by Province';

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-0">Dashboard</h1>
          <small className="text-muted">{scopeSubtitle}</small>
        </div>
        <Button variant="outline-primary" size="sm" onClick={loadData} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-1"></i>Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="warning" dismissible onClose={() => setError(null)} className="mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>{error}
        </Alert>
      )}

      {/* Row 1 — Record stat cards */}
      <Row className="mb-3 g-3">
        <Col lg={3} md={6}>
          <DashStatCard title="Total Records"   value={dash?.totalRecords}     icon="bi-database-fill"      color="info"    subtitle="Registered persons in DB" loading={loading} />
        </Col>
        <Col lg={3} md={6}>
          <DashStatCard title="This Month"      value={dash?.recordsThisMonth} icon="bi-calendar-check-fill" color="success" subtitle="New registrations"        loading={loading} />
        </Col>
        <Col lg={3} md={6}>
          <DashStatCard title="This Week"       value={dash?.recordsThisWeek}  icon="bi-graph-up-arrow"      color="primary" subtitle="Last 7 days"              loading={loading} />
        </Col>
        <Col lg={3} md={6}>
          <DashStatCard title="DS Divisions"    value={dash?.totalDSDs}        icon="bi-geo-alt-fill"        color="warning" subtitle="Covered locations"        loading={loading} />
        </Col>
      </Row>

      {/* Row 2 — User stat cards */}
      <Row className="mb-4 g-3">
        <Col lg={3} md={6}>
          <DashStatCard title="Total System Users" value={totalUsers}     icon="bi-people-fill"       color="primary" subtitle={`${adminCount} admin · ${editorCount} editor · ${viewerCount} viewer`} loading={loading} />
        </Col>
        <Col lg={3} md={6}>
          <DashStatCard title="Active Users"       value={activeUsers}    icon="bi-person-check-fill" color="success" subtitle="Approved accounts"     loading={loading} />
        </Col>
        <Col lg={3} md={6}>
          <DashStatCard title="Pending Approvals"  value={pendingUsers}   icon="bi-hourglass-split"   color="warning" subtitle="Awaiting admin review" loading={loading} />
        </Col>
        <Col lg={3} md={6}>
          <DashStatCard title="Suspended"          value={suspendedUsers} icon="bi-person-x-fill"     color="danger"  subtitle="Suspended accounts"    loading={loading} />
        </Col>
      </Row>

      {/* Row 3 — Detail cards */}
      <Row className="g-3 mb-4">
        <Col lg={4}>
          <UserBreakdownCard
            loading={loading}
            adminCount={adminCount} editorCount={editorCount} viewerCount={viewerCount}
            activeUsers={activeUsers} pendingUsers={pendingUsers} suspendedUsers={suspendedUsers}
          />
        </Col>
        <Col lg={4}>
          <ProvinceSubmissionsCard loading={loading} provinceData={provinceData} title={breakdownTitle} />
        </Col>
        <Col lg={4}>
          <RecentUsersCard loading={loading} recentUsers={recentUsers} />
        </Col>
      </Row>

      {/* Quick actions */}
      <Row>
        <Col>
          <QuickActionsCard pendingUsers={pendingUsers} />
        </Col>
      </Row>
    </div>
  );
}

export default Dashboard;
